import React, { useState } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Button,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useToast,
  Badge
} from '@chakra-ui/react';
import { HamburgerIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const AdminLayout = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: 'ออกจากระบบสำเร็จ',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      navigate('/login');
    } catch (error) {
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถออกจากระบบได้',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const menuItems = [
    { name: 'ภาพรวม', path: '/admin', icon: '📊' },
    { name: 'สถิติ', path: '/admin/statistics', icon: '📈' },
    { name: 'จัดการผู้ใช้', path: '/admin/users', icon: '👥' },
    { name: 'จัดการหมวดหมู่', path: '/admin/categories', icon: '📁' },
    { name: 'จัดการเนื้อหา', path: '/admin/content', icon: '📄' },
    { name: 'กลับสู่หน้าแรก', path: '/', icon: '🏠' }
  ];

  const isActive = (path) => location.pathname === path;

  const Sidebar = ({ isMobile = false }) => (
    <VStack spacing={0} align="stretch" w={isMobile ? "full" : "250px"} h="100vh" bg="blue.600" color="white">
      <Box p={4} borderBottom="1px" borderColor="blue.500">
        <Text fontSize="lg" fontWeight="bold">
          ระบบจัดการเงินทุนหมุนเวียน
        </Text>
        <Text fontSize="sm" color="blue.200">
          ระบบบริหารจัดการ
        </Text>
      </Box>
      
      <VStack spacing={1} p={2} flex="1">
        {menuItems.map((item) => (
          <Button
            key={item.path}
            variant={isActive(item.path) ? "solid" : "ghost"}
            colorScheme={isActive(item.path) ? "blue" : "transparent"}
            bg={isActive(item.path) ? "blue.500" : "transparent"}
            color={isActive(item.path) ? "white" : "blue.100"}
            justifyContent="flex-start"
            w="100%"
            h="40px"
            onClick={() => {
              navigate(item.path);
              if (isMobile) setIsOpen(false);
            }}
            _hover={{
              bg: isActive(item.path) ? "blue.500" : "blue.500",
              color: "white"
            }}
          >
            <Text mr={2}>{item.icon}</Text>
            {item.name}
          </Button>
        ))}
      </VStack>
    </VStack>
  );

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Mobile Header */}
      <Flex
        display={{ base: "flex", md: "none" }}
        bg="white"
        p={4}
        shadow="sm"
        justify="space-between"
        align="center"
      >
        <HStack>
          <IconButton
            icon={<HamburgerIcon />}
            variant="ghost"
            onClick={() => setIsOpen(true)}
            aria-label="Open menu"
          />
          <Text fontSize="lg" fontWeight="bold" color="blue.600">
            ระบบจัดการ
          </Text>
        </HStack>
        
        <Menu>
          <MenuButton as={Button} variant="ghost" rightIcon={<ChevronDownIcon />}>
            <HStack>
              <Text>{user?.full_name || user?.username}</Text>
              <Badge colorScheme="green" variant="subtle">
                {user?.role === 'admin' ? 'ผู้ดูแล' : 'ผู้ใช้'}
              </Badge>
            </HStack>
          </MenuButton>
          <MenuList>
            <MenuItem onClick={handleLogout}>ออกจากระบบ</MenuItem>
          </MenuList>
        </Menu>
      </Flex>

      <Flex>
        {/* Desktop Sidebar */}
        <Box display={{ base: "none", md: "block" }}>
          <Sidebar />
        </Box>

        {/* Mobile Drawer */}
        <Drawer isOpen={isOpen} onClose={() => setIsOpen(false)} placement="left">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader>เมนู</DrawerHeader>
            <DrawerBody p={0}>
              <Sidebar isMobile />
            </DrawerBody>
          </DrawerContent>
        </Drawer>

        {/* Main Content */}
        <Box flex="1" p={{ base: 4, md: 6 }}>
          {/* Desktop Header */}
          <Flex
            display={{ base: "none", md: "flex" }}
            justify="space-between"
            align="center"
            mb={6}
            p={4}
            bg="white"
            rounded="lg"
            shadow="sm"
          >
            <Text fontSize="xl" fontWeight="bold" color="gray.700">
              {menuItems.find(item => isActive(item.path))?.name || 'ภาพรวม'}
            </Text>
            
            <Menu>
              <MenuButton as={Button} variant="ghost" rightIcon={<ChevronDownIcon />}>
                <HStack>
                  <Text>{user?.full_name || user?.username}</Text>
                  <Badge colorScheme="green" variant="subtle">
                    {user?.role === 'admin' ? 'ผู้ดูแล' : 'ผู้ใช้'}
                  </Badge>
                </HStack>
              </MenuButton>
              <MenuList>
                <MenuItem onClick={handleLogout}>ออกจากระบบ</MenuItem>
              </MenuList>
            </Menu>
          </Flex>

          {/* Page Content */}
          <Box>
            <Outlet />
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};

export default AdminLayout;

